import { Inject, Injectable } from '@angular/core';
import { CONFIG } from '@domgen/dgx-fe-config';
import posthog, { JsonType } from 'posthog-js';
import { Observable, of } from 'rxjs';
import { AppConfig } from '../models/config/app-config.type';
import { AppRuntimeFeatures } from '../models/runtime/app-runtime-features.interface';

// The below FeatureFlagKey values must match exactly as-is with how they are named in Posthog
export type FeatureFlagKey =
  | 'whp-confirmation-banner'
  | 'maintenance-banner'
  | 'smart-fix-genai'
  | 'hoover-candy-tracker-enabled'
  | 'vulnerable-customers-form'
  | 'bekoSalesforceMigration_RepairDetailsOutage'
  | 'bekoSalesforceMigration_SalesforceOutage'
  | 'smart-fix-hub-banner'
  | 'bekoSalesforceMigration_FullOutage'
  | 'CLAIMS_2_0'
  | 'bedrock-rag-optimization'
  | 'smart-fix-genai-chatbot-boiler'
  | 'CLAIMS_2_0_HEATING'
  | 'mmr-feedback-capture'
  | 'claims-2-0-orbit-payment';

export type RunTimeFeatureKeys = keyof AppRuntimeFeatures;
@Injectable({
  providedIn: 'root',
})
export class FeatureFlagService {
  initialised = false;
  featureFlags: Partial<Record<FeatureFlagKey, boolean>> = {};

  constructor(@Inject(CONFIG) private readonly config: AppConfig) {
    this.initialise();
  }

  initialise(): void {
    posthog.onFeatureFlags(
      (_flags: string[], variants: Record<string, string | boolean>) => {
        this.featureFlags = variants as Record<FeatureFlagKey, boolean>;
        this.initialised = true;
      }
    );
  }

  isFeatureFlagEnabled(
    featureFlagKey: FeatureFlagKey,
    configFallbackKey?: RunTimeFeatureKeys
  ): boolean {
    // Save calls to posthog outside of initial load
    if (this.initialised) {
      return (
        this.featureFlags[featureFlagKey] ??
        this.config.runtimeFeatures[configFallbackKey as any] ??
        false
      );
    }

    return (
      posthog.isFeatureEnabled(featureFlagKey) ??
      this.config.runtimeFeatures[configFallbackKey as any] ??
      false
    );
  }

  isFeatureFlagEnabled$(
    featureFlagKey: FeatureFlagKey,
    configFallbackKey?: RunTimeFeatureKeys
  ): Observable<boolean> {
    // Save calls to posthog outside of initial load
    if (this.initialised) {
      return of(
        this.featureFlags[featureFlagKey] ??
          this.config.runtimeFeatures[configFallbackKey as any] ??
          false
      );
    }

    const value = posthog.isFeatureEnabled(featureFlagKey);

    return of(
      value ?? this.config.runtimeFeatures[configFallbackKey as any] ?? false
    );
  }

  getFeatureFlagPayload(featureFlagKey: FeatureFlagKey): JsonType | null {
    if (this.initialised) {
      if (this.featureFlags[featureFlagKey]) {
        return posthog.getFeatureFlagPayload(featureFlagKey);
      }
      return null;
    }

    if (posthog.isFeatureEnabled(featureFlagKey)) {
      return posthog.getFeatureFlagPayload(featureFlagKey);
    }

    return null;
  }

  getFeatureFlagPayload$(
    featureFlagKey: FeatureFlagKey
  ): Observable<JsonType | null> {
    if (this.initialised) {
      const payload =
        this.featureFlags[featureFlagKey] &&
        posthog.getFeatureFlagPayload(featureFlagKey);
      return of(payload ?? null);
    }

    const payload =
      posthog.isFeatureEnabled(featureFlagKey) &&
      posthog.getFeatureFlagPayload(featureFlagKey);
    return of(payload ?? null);
  }
}
